import {Injectable, NgZone} from '@angular/core';
import {
    ActivityMessage,
    ActivityService,
    ActivityStatus,
    ConfigService,
    LocaleService,
    MessageConstants,
    PlayerConfig,
    Scorm,
    ScormParamsConstants,
    ShellMessage
} from '@transparent/coretypes';
import {ActivatedRoute, Router} from '@angular/router';
import {convertActivityStatus, goMobile, saveAltAnswer} from '../utils/lms-util';
import {ActivityVO} from '../vo/activity-vo';
import {getActivitySkill} from '../utils/icons';
import {Observable, of, from} from 'rxjs';
import {MoneCommunicationService} from './mone-communication.service';
import {ActivityReport, CompleteItReport, MlnReport} from '@transparent/cw-api';

const completePopups = ['activity-complete', 'list-complete', 'unit-complete', 'learned-complete', 'alphabet-complete'];

@Injectable()
export class MessageService {
    isMenuOpen = false;
    mouseDownTime = 0;
    outletMaps: { [key: string]: string; } = {};
    private _hasPopup: boolean;
    private appConfig: PlayerConfig;

    constructor(private msg: ActivityService,
                private config: ConfigService,
                private communication: MoneCommunicationService,
                private locale: LocaleService,
                private router: Router,
                private zone: NgZone) {
        this.appConfig = this.config.upConfig;
    }

    /**
     * Returns if router url has ae popup
     * {boolean}
     */
    get isAeOpen(): boolean {
        return (/aex:explosion/).test(this.router.url);
    }

    get isCompleteOpen(): boolean {
        return (/\(popup:popup-manager\)\?popupName=(activity-complete|list-complete|unit-complete|learned-complete|alphabet-complete)/gmi)
            .test(this.router.url) || !!Object.values(this.outletMaps || {}).find(popupName => completePopups.includes(popupName));
    }


    /**
     * Returns if router url has ae popup
     * {boolean}
     */
    get isRefOpen(): boolean {
        return (/reference:reference/).test(this.router.url);
    }

    /**
     * Returns if router url has pdf view
     * {boolean}
     */
    get isPdfOpen(): boolean {
        return (/reference:pdf/).test(this.router.url);
    }

    /**
     * Returns if router url has help popup
     * {boolean}
     */
    get isHelpOpen(): boolean {
        return (/help:help-popup-manager/).test(this.router.url);
    }

    /**
     * hasPopup
     * {boolean} value
     */

    set hasPopup(value: boolean) {
        if (this._hasPopup !== value) {
            this._hasPopup = value;
            if (value) {
                this.sendPauseMessage();
            } else {
                this.sendResumeMessage();
            }
        }
    }

    get hasPopup(): boolean {
        return this._hasPopup;
    }


    clean(): void {
        this.appConfig = null;
        this.outletMaps = null;
    }

    hasPopupOutlet(): boolean {
        const root: ActivatedRoute = this.router.routerState.root;
        const pat = /(popup|about|help|alphabet-popup|pdfPopup|setting-spopup)/g;
        for (let i = 0; i < root.children.length; i++) {
            const item: ActivatedRoute = root.children[i];
            if (pat.test(item.outlet)) {
                return true;
            }
        }
        return false;
    }

    handleActivityReport(report: ActivityReport | CompleteItReport | MlnReport): Observable<any> {
        if (report) {
            if ('name' in report && !report.skill) {
                report.moduleName = report.moduleName || '';
                const moduleName = report.moduleName.replace(/A$/, '');
                report.skill = getActivitySkill(moduleName, new ActivityVO(), null);
            }
            report.completionStatus = convertActivityStatus(report.completionStatus);
            if (this.communication.isCWAPI) {
                if ('passages' in report) {
                    return from(this.communication.cwapi.report.saveCompleteItReport(report as CompleteItReport));
                } else if (Array.isArray(report.challenges)) {
                    return from(this.communication.cwapi.report.saveMlnReport(report as MlnReport));
                }
                return from(this.communication.cwapi.report.saveActivityReport(report as ActivityReport));
            } else {
                let cmiParam = ScormParamsConstants.ACTIVITY_REPORT;
                if ('passages' in report) {
                    cmiParam = ScormParamsConstants.COMPLETE_IT_REPORT;
                } else if (Array.isArray(report.challenges)) {
                    cmiParam = ScormParamsConstants.MLN_REPORT;
                }
                return Scorm.callScormMethod('SetValue', cmiParam, JSON.stringify(report));
            }
        }
        return of('');
    }

    /**
     * Handles sent messages to shell
     * {Object} msg
     * {ActivityVO} act
     * {boolean} isReference
     * {Object} player
     */
    sendMessageToShell(msg: ShellMessage, act: ActivityVO, isReference = false, player: any): void {
        switch (msg.name) {
            case MessageConstants.SHOW_ERROR_POPUP:
                player.resolver.error = msg.data;
                this.router.navigate(['', {outlets: {popup: 'popup-manager'}}],
                    {skipLocationChange: true, queryParams: {popupName: 'error'}})
                    .catch((e) => console.log(e));
                break;
            case MessageConstants.SOUND_ERROR_MESSAGE:
                act.percentage = 100;
                act.status = ActivityStatus.COMPLETED;
                this.router.navigate(['', {outlets: {popup: 'popup-manager'}}],
                    {skipLocationChange: true, queryParams: {popupName: 'required-sound'}})
                    .catch((e) => console.log(e));
                break;
            case MessageConstants.ACTIVITY_OVERLAY_STATE:
                break;
            case MessageConstants.SEND_ALT_ANSWER:
                saveAltAnswer(msg.data);
                break;
            case MessageConstants.SHOW_AE:
                this.sendPauseMessage();
                this.msg.aeData = msg.data;
                this.router.navigate(['', {outlets: {aex: 'explosion'}}])
                    .catch((e) => console.log(e));
                break;
            case MessageConstants.CLOSE_AE:
                if (!isReference) {
                    this.sendResumeMessage();
                }
                break;
            case MessageConstants.SHOW_IME_POPUP :
                if (msg.data.showPopup) {
                    const popupName = (/japanese/i).test(msg.data.languageCode) ? 'japanese-ime' : 'ime-alert';
                    this.router.navigate(['', {outlets: {'settings-popup': 'settings-popup-manager'}}],
                        {skipLocationChange: true, queryParams: {popupName}})
                        .catch((e) => console.log(e));
                }
                break;
            case MessageConstants.SHOW_ACTIVITY_HELP :
                this.config.currentHelpUrl = msg.data;
                this.router.navigate(['', {outlets: {help: 'help-popup-manager'}}],
                    {skipLocationChange: true, queryParams: {popupName: 'help'}})
                    .catch((e) => console.log(e));
                break;
            case MessageConstants.GO_MOBILE:
                goMobile(this.appConfig);
                break;
            case MessageConstants.SOUND_VOLUME:
                player.volume = msg.data.volume;
                if (msg.data.muted) {
                    player.volume = 0;
                }
                if (player.audio) {
                    player.audio.volume = player.volume;
                }
                try {
                    sessionStorage.setItem('volume', '' + player.volume);
                } catch (e) {
                }
                break;
        }
    }

    /**
     * Sends pause message to modules
     */
    sendPauseMessage(): void {
        const msg: ActivityMessage = {name: MessageConstants.PAUSE_ACTIVITY, body: this.isRefOpen};
        console.log('sending PAUSE_ACTIVITY MESSAGE');
        this.zone.run(() => {
            if (this.isAeOpen) {
                this.msg.aeChange.next(msg);
            } else {
                this.msg.sendMessageToModule.next(msg);
            }
        });
    }

    /**
     * Sends resume message to modules
     */
    sendResumeMessage(): void {
        if (this.hasPopupOutlet() || this.isMenuOpen || this.isPdfOpen) {
            return;
        }
        const msg: ActivityMessage = {name: MessageConstants.RESUME_ACTIVITY, body: this.isRefOpen};
        console.log('sending RESUME_ACTIVITY MESSAGE');
        this.zone.run(() => {
            if (this.isAeOpen) {
                this.msg.aeChange.next(msg);
            } else {
                this.msg.sendMessageToModule.next(msg);
            }
        });
    }
}
